import { forwardRef } from 'react';
import { getCardBadge } from '../utils/deckUtils';

const FlashCard = forwardRef(({
  currentCard,
  currentIndex,
  deckLength,
  isFlipped,
  isDragging,
  dragX,
  swipeDirection,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerLeave,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
}, ref) => {
  const rotation = dragX * 0.1;
  const opacity = Math.max(0.5, 1 - Math.abs(dragX) / 300);

  const getSwipeIndicator = () => {
    if (dragX > 50) return { text: '记住了', bg: 'var(--yes)', fg: 'var(--yes-ink)', side: 'left', opacity: Math.min(1, (dragX - 50) / 100) };
    if (dragX < -50) return { text: '再学习', bg: 'var(--no)', fg: 'var(--no-ink)', side: 'right', opacity: Math.min(1, (-dragX - 50) / 100) };
    return null;
  };

  const indicator = getSwipeIndicator();
  const badge = getCardBadge(currentCard);
  const isCustomCard = currentCard.deckId === 'custom';
  const isPhrase = currentCard.deckId === 'connectors';

  const faceClass = `flashcard-face${isCustomCard ? ' flashcard-custom' : ''}`;
  const backClass = `flashcard-face flashcard-back${isCustomCard ? ' flashcard-custom' : ''}`;

  const badgeStyle = {
    background: isCustomCard ? 'var(--lime)' : `${badge.color}33`,
    borderColor: 'var(--line)',
  };

  return (
    <div
      ref={ref}
      className="flashcard"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerLeave}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{
        transition: isDragging ? 'none' : 'transform 0.3s ease, opacity 0.3s ease',
        transform: swipeDirection
          ? `translateX(${swipeDirection === 'right' ? '420px' : '-420px'}) rotate(${swipeDirection === 'right' ? '26deg' : '-26deg'})`
          : `translateX(${dragX}px) rotate(${rotation}deg) ${isFlipped ? 'rotateY(180deg)' : ''}`,
        opacity: swipeDirection ? 0 : opacity,
      }}
    >
      {/* Front */}
      <div className={faceClass}>
        {indicator && (
          <div
            className="swipe-stamp"
            style={{
              background: indicator.bg,
              color: indicator.fg,
              opacity: indicator.opacity,
              [indicator.side]: '18px',
              transform: `rotate(${dragX > 0 ? '-12deg' : '12deg'})`,
            }}
          >
            {indicator.text}
          </div>
        )}

        <div className={isPhrase ? 'card-char-front card-char-front-phrase' : 'card-char-front'}>
          {currentCard.char}
        </div>

        <div className="card-badge" style={badgeStyle}>
          <span style={{ color: isCustomCard ? 'var(--lime-ink)' : badge.color }}>{badge.label}</span>
          {!isCustomCard && <span style={{ opacity: 0.65 }}>#{currentCard.originalIndex + 1}</span>}
        </div>

        <div className="card-rank">{currentIndex + 1} / {deckLength}</div>

        <div className="card-hint">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ verticalAlign: '-2px', marginRight: '5px' }}>
            <path d="M8 13V5a2 2 0 1 1 4 0v6" />
            <path d="M12 11a2 2 0 1 1 4 0v2" />
            <path d="M16 12a2 2 0 1 1 4 0v3a6 6 0 0 1-6 6h-2a7 7 0 0 1-7-7v-1a2 2 0 1 1 4 0" />
          </svg>
          点击翻转
        </div>
      </div>

      {/* Back */}
      <div className={backClass}>
        <div className="card-badge" style={badgeStyle}>
          <span style={{ color: isCustomCard ? 'var(--lime-ink)' : badge.color }}>{badge.label}</span>
          {!isCustomCard && <span style={{ opacity: 0.65 }}>#{currentCard.originalIndex + 1}</span>}
        </div>

        <div className={isPhrase ? 'card-char-back card-char-back-phrase' : 'card-char-back'}>
          {currentCard.char}
        </div>

        <div className="card-pinyin">{currentCard.pinyin}</div>

        <div className="card-meaning">{currentCard.meaning}</div>

        {currentCard.example && (
          <>
            <div className="card-divider" />
            <div className="card-example">
              <strong style={{ color: 'var(--coral)' }}>例:</strong> {currentCard.example}
            </div>
          </>
        )}
      </div>
    </div>
  );
});

FlashCard.displayName = 'FlashCard';

export default FlashCard;
